// MOCK FOR ONBOARDING TUTORIAL PURPOSES
import { makeTrpcClient } from "@common/api-interface/trpc-client";
import { CommonEvents } from "@common/services/common-events";
import { makeCalendarService } from "@common/services/calendar-event-service";
import { makeRealtimeService } from "@common/services/realtime/realtime-service";
import type { SpaceService } from "@common/services/space-service";
import type { TodoService, TodoServiceEventTypes } from "@common/services/todo-service";
import type { UserService } from "@common/services/user-service";
import CalendarStore from "@common/stores/calendar-store";
import FeaturesStore from "@common/stores/features-store";
import PurchaseStore from "@common/stores/purchase-store";
import RealtimeSessionStore from "@common/stores/realtime-session-store";
import { HASHTAG_LAST_USED_KEY } from "@common/stores/shared-todo-store";
import SpaceStore from "@common/stores/space-store";
import TimerStore from "@common/stores/timer-store";
import UserDetailsStore from "@common/stores/user-details-store";
import UserPreferencesStore, { USER_PREFERENCES_CACHE_KEY } from "@common/stores/user-preferences-store";
import UserStore from "@common/stores/user-store";
import { makeNote } from "@common/types/notes";
import type { Space, SpaceId } from "@common/types/space";
import type { UserProfile } from "@common/types/user";
import type { Http } from "@common/utils/http";
import { AreaSelectStore } from "@web/src/app/components/area-selection/area-select-store";
import { mockPurchasesApi } from "@web/src/app/services/purchases-api";
import type { AllDndTypeBundle } from "@web/src/app/use-cases/drag-and-drop";
import { WebEvents } from "@web/src/app/web-events";
import { DndStore } from "@web/src/hooks/dnd/dnd-store";
import HighlightStore from "@web/src/store/highlight-store";
import UIStore from "@web/src/store/ui-store";
import NotesStore from "@web/src/store/web-notes-store";
import type { Stores } from "@web/src/store/web-stores";
import WebTodoStore from "@web/src/store/web-todo-store";
import { EventEmitter } from "eventemitter3";
import _ from "lodash";

const mockAnalytics = {
  track: () => {},
};
const mockUser: UserProfile = {
  id: "MOCK_USER_ID",
  fullName: "",
  createdDate: 0,
  email: "",
  picture: "",
};
// local storage but with promise return types
const mockLocalStorage = {
  async getItem(key: string) {
    if (key === "accessToken") {
      return "MOCK_ACCESS_TOKEN";
    }

    if (key === "userProfile") return JSON.stringify(mockUser);
    if (key === USER_PREFERENCES_CACHE_KEY) return null;
    if (key === HASHTAG_LAST_USED_KEY) return null;

    throw new Error(`key value not mocked: ${key}`);
  },
  async setItem(key: string, value: string) {},
  async removeItem(key: string) {},
};

const timezone = {
  getTimezone: () => localStorage.getItem("timezone") ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
};

const mockTrpc = makeTrpcClient("", "1.0.0", "web");

export const mockHomeSpace: Space = {
  id: "MOCK_USER_ID", // home
  name: "Home",
  createdBy: "MOCK_USER_ID",
  createdDate: 0,
  icon: "home",
};
export const mockWorkSpace: Space = {
  id: "MOCK_WORK_SPACE_ID", // home
  name: "Work",
  createdBy: "MOCK_USER_ID",
  createdDate: 0,
  icon: "wrench",
};

export function isMockSpaceId(spaceId: SpaceId) {
  return spaceId.startsWith("MOCK_");
}

const mockSpaceService: SpaceService = {
  getSpaces: async () => {
    return [mockHomeSpace, mockWorkSpace];
  },
  createSpace: async () => {
    return {
      id: "",
      name: "",
      createdBy: "",
      createdDate: 0,
      icon: "home",
    };
  },
  updateSpace: async () => false,
  deleteSpace: async () => false,
  getSpaceUsersRoles: async () => [],
  updateUserRole: () => {
    throw new Error("Unimplemented mock: updateUserRole");
  },
  getJoinShareLinkDetails: () => {
    throw new Error("Unimplemented mock: getJoinShareLinkDetails");
  },
  getShareLinkForSpace: () => {
    throw new Error("Unimplemented mock: getShareLinkForSpace");
  },
  getSpace: async () => mockHomeSpace,
  joinSpaceFromShareLink: () => {
    throw new Error("Unimplemented mock: joinSpaceFromShareLink");
  },
};

const mockUserService: UserService = {
  verifyGoogleToken: async () => {
    return {
      success: true,
      token: "",
      user: mockUser,
      isNew: false,
    };
  },
  verifyGoogleAuthCode: async () => {
    return {
      success: true,
      token: "",
      user: mockUser,
      isNew: false,
    };
  },
  verifyAppleToken: async () => {
    return {
      success: true,
      token: "",
      user: mockUser,
      isNew: false,
    };
  },
  setOnboardingSeen: async () => {},
  setWaitlist: async () => {},
  getUser: async () => mockUser,
  getUserIsPremium: async () => true,
  getUserPreferences: async () => null,
  updatePreferences: async () => true,
  getUserReferralInfo: async () => ({
    referralCode: "MOCK_REFERRAL_CODE",
    referralCount: 0,
    hasLifetimeReward: false,
  }),
  deleteUser: async () => true,
};

const mockTodoService: TodoService = {
  createTodo: async () => {},
  updateTodos: async () => {},
  eventEmitter: new EventEmitter<TodoServiceEventTypes>(),
  debouncedDispatch: _.debounce(async () => {}),
  debouncedUpdateNoteForTodo: _.debounce(() => {}),
  deleteNoteForTodo: async () => ({ success: false }),
  dispatchUpdates: async () => {},
  getHistoricalTodos: async () => ({}),
  getNoteForTodo: async () =>
    makeNote({
      owner: "MOCK_USER_ID",
      content: `<div>

    <blockquote>OMG, To-day is a total lifesaver 🙌 It's got my back, keeping me super organized and on top of all my stuff. I legit never miss a thing. It's fire, fr. 🔥✨</blockquote>
    <br/>
    <h3>Aight, here's why To-day slaps:</h3>
    <ol>
      <li>Sleek and on fleek</li>
      <li>Smooth with dem shortcuts</li>
      <li>Also, notes legit bussin</li>
    </ol>
  </div>`,
    }),
  getTodos: async () => ({}),
  getTodosV2: async () => ({}),
  updateNoteForTodo: async () => ({ created_id: undefined }),
  migrateTodos: async () => {
    throw new Error("unimplemented");
  },
  getHashtagsForSpace: async () => [],
  upsertHashtagMetas: async () => {},
  deleteHashtagMeta: async () => {},
};

const mockHttp: Http = {
  json: async () => {
    return {} as any;
  },
  getAccessToken: () => "MOCK_ACCESS_TOKEN",
  setAccessToken: () => {},
};

export const highlightStore = new HighlightStore();
export const userStore = new UserStore({
  commonEvents: CommonEvents,
  localStorage: mockLocalStorage,
  timezone,
  userService: mockUserService,
  purchasesApi: mockPurchasesApi,
  onAccessTokenChange: () => {},
  getAccessToken: () => "MOCK_ACCESS_TOKEN",
});
export const uiStore = new UIStore({ highlightStore, userStore, commonEvents: CommonEvents });
export const userPreferencesStore = new UserPreferencesStore({
  localStorage: mockLocalStorage,
  userService: mockUserService,
});
export const spaceStore = new SpaceStore({
  spaceService: mockSpaceService,
  userStore,
  makeTodoStore: (spaceId: string, spaceStore: SpaceStore) =>
    new WebTodoStore(spaceId, {
      userStore,
      todoService: mockTodoService,
      spaceStore,
      commonEvents: CommonEvents,
      webEvents: WebEvents,
      highlightStore,
      analytics: mockAnalytics,
      localStorage: mockLocalStorage,
    }),
  userPreferencesStore,
  commonEvents: CommonEvents,
});
export const timerStore = new TimerStore({
  todos: {
    getTodoById: () => null,
    getNextTodo: () => null,
    updateTodo: () => {},
    setTodoComplete: () => {},
  },
  notify: {
    breakOver: () => {},
    breakStart: () => {},
    todoComplete: () => {},
    timerStart: () => {},
  },
  localStorage: {
    getItem: async () => null,
    setItem: async () => {},
    removeItem: async () => {},
  },
  getDisplayMode: () => spaceStore.currentTodoStore.displayMode,
});
export const notesStore = new NotesStore({ spaceStore, todoService: mockTodoService, userStore });

const featuresStore = new FeaturesStore({
  featureFlagService: {
    initialize: async () => {},
    getFeatureEnabled: () => false,
  },
});

const dndStore = new DndStore<AllDndTypeBundle>({
  space: {
    handleDrop: () => {},
    kind: "space",
  },
  add_todo_button: {
    handleDrop: () => {},
    kind: "add_todo_button",
  },
  todo: {
    handleDrop: () => {},
    kind: "todo",
  },
});

const mockRealtimeService = makeRealtimeService({
  getAccessToken: () => "MOCK_ACCESS_TOKEN",
});

const mockEventService = makeCalendarService({
  trpc: mockTrpc.client,
});

const mockStores: Stores = {
  calendarStore: new CalendarStore({
    eventService: mockEventService,
    userPreferencesStore,
    userStore,
    spaceProvider: spaceStore,
  }),
  userStore,
  uiStore,
  timerStore,
  dndStore,
  spaceStore,
  notesStore,
  highlightStore,
  userDetailsStore: new UserDetailsStore({ trpcClient: mockTrpc.client }),
  purchaseStore: new PurchaseStore({
    purchasesApi: mockPurchasesApi,
  }),
  featuresStore,
  userPreferencesStore,
  areaSelectStore: new AreaSelectStore(),
  realtimeSessionStore: new RealtimeSessionStore({
    realtimeService: mockRealtimeService,
  }),
};

export default mockStores;
